import { Box, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useRef, useState, type ReactNode } from "react";

import { Markdown } from "./markdown.js";

type Mode = "write" | "preview";

interface ToolbarAction {
  label: string;
  title: string;
  /** Wrap the selection with `before`/`after` (inline marks like bold). */
  wrap?: [string, string];
  /** Prepend `prefix` to the start of the current line (headings, lists). */
  linePrefix?: string;
}

const TOOLBAR: ToolbarAction[] = [
  { label: "H2", title: "Heading", linePrefix: "## " },
  { label: "B", title: "Bold", wrap: ["**", "**"] },
  { label: "I", title: "Italic", wrap: ["*", "*"] },
  { label: "“ ”", title: "Quote", linePrefix: "> " },
  { label: "• List", title: "Bulleted list", linePrefix: "- " },
  { label: "1. List", title: "Numbered list", linePrefix: "1. " },
  { label: "Link", title: "Link", wrap: ["[", "](https://)"] },
  { label: "Code", title: "Inline code", wrap: ["`", "`"] },
];

/**
 * Markdown editor with a **Write / Preview** toggle and a small formatting
 * toolbar. Built on the shared in-house `<Markdown>` renderer so the
 * preview matches exactly what the reader will show — no second markdown
 * dialect to keep in sync.
 *
 * Replaces the bare monospace textarea in the manual create/edit form: you
 * could type raw markdown but never see it rendered and had no help with
 * the syntax ("the markup is all shit" — reported 2026-06-06). The toolbar
 * wraps the current selection (bold/italic/link/code) or prefixes the
 * current line (heading/quote/lists); Preview renders the live body.
 *
 * In Write mode it renders a MUI `TextField` carrying the supplied `label`
 * + `id`, so existing label-based selectors keep working.
 */
export function MarkdownEditor({
  id,
  label,
  value,
  onChange,
  placeholder,
  rows = 24,
  helperText,
}: {
  id?: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
  helperText?: ReactNode;
}): JSX.Element {
  const [mode, setMode] = useState<Mode>("write");
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const restoreSelection = (start: number, end: number): void => {
    const el = ref.current;
    if (!el) return;
    // Defer so the re-render with the new value has committed.
    const raf =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame
        : (cb: () => void) => setTimeout(cb, 0);
    raf(() => {
      el.focus();
      try {
        el.setSelectionRange(start, end);
      } catch {
        /* selection APIs can throw on detached nodes — harmless */
      }
    });
  };

  const apply = (action: ToolbarAction): void => {
    const el = ref.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;

    if (action.wrap) {
      const [before, after] = action.wrap;
      const selected = value.slice(start, end);
      const next = value.slice(0, start) + before + selected + after + value.slice(end);
      onChange(next);
      restoreSelection(start + before.length, start + before.length + selected.length);
      return;
    }

    if (action.linePrefix) {
      const lineStart = value.lastIndexOf("\n", start - 1) + 1;
      const next = value.slice(0, lineStart) + action.linePrefix + value.slice(lineStart);
      onChange(next);
      const shift = action.linePrefix.length;
      restoreSelection(start + shift, end + shift);
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, minHeight: 30 }}>
          {mode === "write" &&
            TOOLBAR.map((a) => (
              <Box
                key={a.label}
                component="button"
                type="button"
                aria-label={a.title}
                title={a.title}
                onClick={() => apply(a)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: 26,
                  px: 0.75,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 0.75,
                  bgcolor: "background.default",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: a.label === "B" ? 700 : 500,
                  fontStyle: a.label === "I" ? "italic" : "normal",
                  color: "text.secondary",
                  "&:hover": { bgcolor: "action.hover", color: "text.primary" },
                }}
              >
                {a.label}
              </Box>
            ))}
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, next: Mode | null) => {
            if (next) setMode(next);
          }}
          sx={{ "& .MuiToggleButton-root": { px: 1.25, py: 0.25, fontSize: 12, textTransform: "none" } }}
        >
          <ToggleButton value="write" aria-label="Write">
            Write
          </ToggleButton>
          <ToggleButton value="preview" aria-label="Preview">
            Preview
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === "write" ? (
        <TextField
          {...(id ? { id } : {})}
          label={label}
          multiline
          rows={rows}
          {...(placeholder !== undefined ? { placeholder } : {})}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputRef={ref}
          fullWidth
          sx={{ "& .MuiInputBase-input": { fontFamily: "ui-monospace, monospace", fontSize: 12 } }}
        />
      ) : (
        <Box
          aria-label={`${label} preview`}
          sx={{
            minHeight: rows * 20,
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.3),
            px: 2,
            py: 1.5,
            overflow: "auto",
          }}
        >
          {value.trim().length > 0 ? (
            <Box sx={{ maxWidth: "76ch" }}>
              <Markdown source={value} />
            </Box>
          ) : (
            <Box sx={{ fontSize: 14, color: "text.disabled" }}>Nothing to preview yet.</Box>
          )}
        </Box>
      )}

      {helperText}
    </Box>
  );
}
